import { createTheme } from '@mui/material/styles';

// FIDDUX Design System from Figma - DARK MODE
const fidduxTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#2af598', // Brand primary
      light: '#3adecc',
      dark: '#009658', // Tertiary
      contrastText: '#000000',
    },
    secondary: {
      main: '#00ba2d', // Brand secondary
      light: '#2af598',
      dark: '#009658',
      contrastText: '#ffffff',
    },
    success: {
      main: '#00c820', // Positive
      light: '#2af598',
      dark: '#009658',
    },
    error: {
      main: '#f52a2d', // Negative
      light: '#ff6b6b',
      dark: '#d32f2f',
    },
    warning: {
      main: '#ff9800',
      light: '#ffb74d',
      dark: '#f57c00',
    },
    info: {
      main: '#3adecc',
      light: '#4fc3f7',
      dark: '#1976d2',
    },
    background: {
      default: '#1e1e1e', // Dark green from design tokens
      paper: '#353535', // Dark grey
      secondary: '#022113', // Dark green gradient start
    },
    text: {
      primary: '#ffffff', // Light text
      secondary: '#aaaaaa', // Disabled color
      disabled: '#aaaaaa',
    },
    grey: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#eeeeee',
      300: '#e0e0e0',
      400: '#bdbdbd',
      500: '#9e9e9e',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
    },
    divider: 'rgba(255, 255, 255, 0.12)',
  },
  typography: {
    fontFamily: '"Helvetica Neue", "Arial", sans-serif',
    h1: {
      fontFamily: '"Cormorant Garamond", serif',
      fontSize: '48px',
      fontWeight: 500,
      fontStyle: 'italic',
      lineHeight: 1.2,
      color: '#ffffff',
    },
    h2: {
      fontFamily: '"Cormorant Garamond", serif',
      fontSize: '40px',
      fontWeight: 500,
      fontStyle: 'italic',
      lineHeight: 1.2,
      color: '#ffffff',
    },
    h3: {
      fontFamily: '"Cormorant Garamond", serif',
      fontSize: '32px',
      fontWeight: 500,
      fontStyle: 'italic',
      lineHeight: 1.2,
      color: '#ffffff',
    },
    h4: {
      fontFamily: '"Cormorant Garamond", serif',
      fontSize: '24px',
      fontWeight: 500,
      fontStyle: 'italic',
      lineHeight: 1.2,
      color: '#ffffff',
    },
    h5: {
      fontFamily: '"Cormorant Garamond", serif',
      fontSize: '20px',
      fontWeight: 500,
      fontStyle: 'italic',
      lineHeight: 1.2,
      color: '#ffffff',
    },
    h6: {
      fontFamily: '"Cormorant Garamond", serif',
      fontSize: '18px',
      fontWeight: 500,
      fontStyle: 'italic',
      lineHeight: 1.2,
      color: '#ffffff',
    },
    body1: {
      fontFamily: '"Helvetica Neue", "Arial", sans-serif',
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: 1.5,
      color: '#ffffff',
    },
    body2: {
      fontFamily: '"Helvetica Neue", "Arial", sans-serif',
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: 1.5,
      color: '#ffffff',
    },
    button: {
      fontFamily: '"Helvetica Neue", "Arial", sans-serif',
      fontSize: '16px',
      fontWeight: 400,
      textTransform: 'none',
    },
    caption: {
      fontFamily: '"Helvetica Neue", "Arial", sans-serif',
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: 1.2,
      textTransform: 'uppercase',
      color: '#aaaaaa',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          textTransform: 'none',
          fontWeight: 400,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(42, 245, 152, 0.3)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #3adecc 0%, #2af598 100%)',
          color: '#ffffff',
          '&:hover': {
            background: 'linear-gradient(135deg, #2af598 0%, #00ba2d 100%)',
          },
        },
        outlined: {
          borderColor: '#2af598',
          color: '#2af598',
          '&:hover': {
            borderColor: '#00ba2d',
            backgroundColor: 'rgba(42, 245, 152, 0.1)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          background: 'linear-gradient(135deg, #353535 0%, #1e1e1e 100%)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '20px',
          fontWeight: 500,
        },
        colorPrimary: {
          backgroundColor: '#2af598',
          color: '#ffffff',
        },
        colorSecondary: {
          backgroundColor: '#00ba2d',
          color: '#ffffff',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#2af598',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#2af598',
              borderWidth: '2px',
            },
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(42, 245, 152, 0.2)',
          borderBottom: '2px solid #2af598',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          color: '#ffffff',
          borderBottom: '1px solid rgba(42, 245, 152, 0.3)',
        },
        body: {
          color: '#ffffff',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          background: 'linear-gradient(135deg, #353535 0%, #1e1e1e 100%)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        },
      },
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0 2px 4px rgba(0, 0, 0, 0.1)',
    '0 4px 8px rgba(0, 0, 0, 0.1)',
    '0 8px 16px rgba(0, 0, 0, 0.1)',
    '0 16px 32px rgba(0, 0, 0, 0.1)',
    '0 32px 64px rgba(0, 0, 0, 0.1)',
    '0 4px 20px rgba(42, 245, 152, 0.2)',
    '0 8px 32px rgba(42, 245, 152, 0.2)',
    '0 16px 64px rgba(42, 245, 152, 0.2)',
    '0 32px 128px rgba(42, 245, 152, 0.2)',
    '0 4px 20px rgba(42, 245, 152, 0.3)',
    '0 8px 32px rgba(42, 245, 152, 0.3)',
    '0 16px 64px rgba(42, 245, 152, 0.3)',
    '0 32px 128px rgba(42, 245, 152, 0.3)',
    '0 4px 20px rgba(42, 245, 152, 0.4)',
    '0 8px 32px rgba(42, 245, 152, 0.4)',
    '0 16px 64px rgba(42, 245, 152, 0.4)',
    '0 32px 128px rgba(42, 245, 152, 0.4)',
    '0 4px 20px rgba(42, 245, 152, 0.5)',
    '0 8px 32px rgba(42, 245, 152, 0.5)',
    '0 16px 64px rgba(42, 245, 152, 0.5)',
    '0 32px 128px rgba(42, 245, 152, 0.5)',
    '0 4px 20px rgba(42, 245, 152, 0.6)',
    '0 8px 32px rgba(42, 245, 152, 0.6)',
    '0 16px 64px rgba(42, 245, 152, 0.6)',
    '0 32px 128px rgba(42, 245, 152, 0.6)',
  ],
});

export default fidduxTheme;
